import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  EventAvailable as AvailabilityIcon,
  Celebration as CelebrationIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { authService } from '../../services/firebase/auth.service';

interface EmployeeWelcomeBannerProps {
  onDismiss: () => void;
}

export default function EmployeeWelcomeBanner({ onDismiss }: EmployeeWelcomeBannerProps) {
  const { user, updateUserProfile } = useAuth();
  const { organization } = useOrganization();
  const [isVisible, setIsVisible] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const handleDismiss = async () => {
    if (!user?.uid) return;

    try {
      setIsDismissing(true);

      if (dontShowAgain) {
        await authService.dismissWelcomeBanner(user.uid);
        await updateUserProfile({ welcomeBannerDismissed: true });
      }

      setIsVisible(false);
      // Small delay to allow collapse animation
      setTimeout(() => {
        onDismiss();
      }, 300);
    } catch (error) {
      console.error('Error dismissing banner:', error);
      setIsVisible(false);
      onDismiss();
    } finally {
      setIsDismissing(false);
    }
  };

  return (
    <Collapse in={isVisible}>
      <Paper
        sx={{
          mb: 3,
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
          position: 'relative',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: '30%',
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)',
          }}
        />

        <Box sx={{ p: { xs: 2, sm: 3 }, position: 'relative' }}>
          {/* Close button */}
          <IconButton
            onClick={handleDismiss}
            disabled={isDismissing}
            sx={{
              position: 'absolute',
              top: { xs: 4, sm: 8 },
              right: { xs: 4, sm: 8 },
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 } }}>
            {/* Icon */}
            <Box
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CelebrationIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: 'white' }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, color: 'white', pr: { xs: 3, sm: 4 } }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Welcome to ShiftGrid!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                You're now part of <strong>{organization?.name}</strong>. Here's how to get started:
              </Typography>

              {/* Quick tips */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    View your schedule
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  <AvailabilityIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Set your availability
                  </Typography>
                </Box>
              </Box>

              {/* Don't show again and dismiss */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      size="small"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        '&.Mui-checked': {
                          color: 'white',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Don't show this again
                    </Typography>
                  }
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleDismiss}
                  disabled={isDismissing}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 2, sm: 2.5 },
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                    },
                  }}
                >
                  Got it!
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
}
